import React from "react";
import { Cpu, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { formatCompact, formatUsd, toNumber } from "./format";

/**
 * How complete the token/cost numbers are, stated plainly.
 *
 * Call counts are complete. Token and cost figures are not: several models return
 * no usage numbers at all, so `estimated_cost_usd` is the cost of the calls we have
 * numbers for — a floor, not the bill. The backend reports the coverage precisely so
 * the UI can say this instead of presenting a partial total as *the* total.
 *
 * The underlying bug (agents not recording usage) is fixed going forward, but calls
 * already logged without usage stay that way, so historical windows will keep showing
 * low coverage for a while. Hiding that would be the same class of lie the old
 * dashboard shipped.
 */
export function CoverageNotice({ totalCalls = 0, callsWithCostData = 0, coveragePct = 0 }) {
  const total = toNumber(totalCalls);
  const covered = toNumber(callsWithCostData);
  const pct = toNumber(coveragePct);

  if (total === 0 || pct >= 99.5) return null;

  const severe = pct < 50;

  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border p-3 ${
        severe
          ? "border-amber-400/30 bg-amber-500/[0.07]"
          : "border-white/10 bg-white/[0.03]"
      }`}
    >
      <Info
        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${severe ? "text-amber-300" : "text-gray-400"}`}
      />
      <p className={`text-xs ${severe ? "text-amber-100/90" : "text-gray-400"}`}>
        Token and cost figures cover{" "}
        <span className="font-semibold tabular-nums">
          {formatCompact(covered)} of {formatCompact(total)}
        </span>{" "}
        calls ({pct.toFixed(0)}%) — the rest were made by models that reported no
        usage. Treat the cost below as a <span className="font-semibold">floor</span>,
        not the bill.
      </p>
    </div>
  );
}

/** Spend and volume per model, so an expensive model is visible as one. */
export function ModelUsageCard({ byModel = [] }) {
  const models = (Array.isArray(byModel) ? byModel : []).filter((m) => m?.model_id);

  return (
    <Card className="overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_45px_rgba(0,0,0,0.4)]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-gray-100">
          <Cpu className="h-5 w-5 text-violet-300" />
          Usage by model
        </CardTitle>
        <CardDescription className="text-gray-300">
          Which models were called, and what they burned.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {models.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-t border-white/5 bg-white/[0.02]">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Model
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Provider
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Calls
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Tokens
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => {
                  const tokens = toNumber(model.total_tokens);
                  return (
                    <tr
                      key={`${model.provider}-${model.model_id}`}
                      className="border-t border-white/5 transition-colors hover:bg-white/[0.03]"
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-100">
                          {model.model_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-medium text-gray-300">
                          {model.provider || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-white">
                        {formatCompact(model.calls)}
                      </td>
                      {/* A model that reports no usage shows a dash, not a zero:
                          "reported nothing" and "used nothing" are different facts. */}
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-300">
                        {tokens > 0 ? formatCompact(tokens) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-300">
                        {toNumber(model.estimated_cost_usd) > 0 ? (
                          formatUsd(model.estimated_cost_usd)
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-10 text-center text-sm text-gray-400">
            No model was called in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default ModelUsageCard;
